import React, { useState, useEffect, useRef } from 'react';
import { API } from '../config';

export default function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [status, setStatus] = useState('');
  const [mailingLists, setMailingLists] = useState([]);
  const [selectedLists, setSelectedLists] = useState(['general']);
  const turnstileRef = useRef(null);

  // Fetch mailing lists
  useEffect(() => {
    const fetchLists = async () => {
      try {
        const response = await fetch(`${API}/newsletter/lists`);
        const data = await response.json();
        setMailingLists(data);
        // Pre-select "general" if it exists
        const generalList = data.find(l => l.slug === 'general');
        if (generalList) {
          setSelectedLists([generalList.slug]);
        }
      } catch (error) {
        console.error('Failed to fetch mailing lists:', error);
      }
    };
    fetchLists();
  }, []);

  useEffect(() => {
    let widgetId = null;
    const renderWidget = () => {
      if (window.turnstile && turnstileRef.current) {
        turnstileRef.current.innerHTML = '';
        widgetId = window.turnstile.render(turnstileRef.current, {
          sitekey: '0x4AAAAAACX5zPRry72pjXTd',
          theme: 'light',
          callback: (token) => {
            setTurnstileToken(token);
          },
          'error-callback': () => {
            setStatus('Verification failed. Please try again.');
          }
        });
      }
    };
    if (window.turnstile) {
      renderWidget();
    } else {
      const checkInterval = setInterval(() => {
        if (window.turnstile) {
          renderWidget();
          clearInterval(checkInterval);
        }
      }, 200);
      return () => clearInterval(checkInterval);
    }
  }, []);

  const handleListToggle = (slug) => {
    setSelectedLists(prev => {
      if (prev.includes(slug)) {
        return prev.filter(s => s !== slug);
      } else {
        return [...prev, slug];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedLists.length === 0) {
      setStatus('Please select at least one mailing list.');
      return;
    }

    if (!turnstileToken) {
      setStatus('Please complete the verification.');
      return;
    }

    setStatus('subscribing');

    try {
      const response = await fetch(`${API}/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          lists: selectedLists,
          turnstile_token: turnstileToken
        })
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setEmail('');
        window.turnstile?.reset();
      } else {
        setStatus(data.detail || 'Failed to subscribe. Please try again.');
      }
    } catch (error) {
      setStatus('Failed to subscribe. Please try again.');
    }
  };

  return (
    <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
      <h4 className="text-gray-900 font-bold text-xl mb-2 flex items-center gap-2"><img src="/logo.png" alt="" className="h-6 w-auto" /> Subscribe Now</h4>
      <p className="text-gray-600 text-sm mb-6">
        Free benefits insights delivered weekly.
      </p>

      {status === 'success' ? (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
          ✓ Check your email to confirm subscription!
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent placeholder-gray-400"
          />

          {/* Mailing Lists Selection */}
          {mailingLists.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Select topics:</label>
              {mailingLists.map(list => (
                <label key={list.id} className="flex items-start gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedLists.includes(list.slug)}
                    onChange={() => handleListToggle(list.slug)}
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{list.name}</div>
                    {list.description && (
                      <div className="text-xs text-gray-600">{list.description}</div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}

          <div className="flex justify-center py-2">
            <div ref={turnstileRef}></div>
          </div>

          {status && status !== 'subscribing' && status !== 'success' && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-xs">
              {status}
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'subscribing'}
            className="w-full bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 px-6 py-3 rounded-lg font-bold transition-all shadow-lg"
          >
            {status === 'subscribing' ? 'Subscribing...' : 'Subscribe Free'}
          </button>
        </form>
      )}
    </div>
  );
}
